import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import { useIsInSheetSurface } from '@/presentation/contexts/SheetSurfaceContext';
import { useIsLandscape } from '@/presentation/responsiveDesign/responsiveDesign';

/**
 * Single source of truth for the content grid column count.
 *
 * The count depends on the *surface* first and the viewport orientation second:
 *
 * - **Sheet / modal grids always get `CONTENT_CONSTANTS.gridLayout.columnsCount`.**
 *   A `SheetService` surface is width-constrained by design (`responsiveDesign.dialog.width`
 *   — 400 px mobile / 500 px desktop), so the landscape count would slice that width into
 *   tiles too short for their content (the send-gift cell loses its coins price row). The
 *   task prompt states the policy directly: "the modals can always have
 *   `CONTENT_CONSTANTS.gridLayout.columnsCount`, but the responsive column count should be
 *   kept for the grids when displayed on pages lists". The discriminator is
 *   `useIsInSheetSurface` (`presentation/contexts/SheetSurfaceContext.tsx`), provided by
 *   `BottomSheetView` / `DialogView` — so every sheet grid is correct by construction rather
 *   than by author discipline, and no call site passes a flag.
 * - **Page grids keep the responsive count**: the landscape column count in landscape
 *   orientation, the portrait/default count otherwise. Both values are read from
 *   `CONTENT_CONSTANTS.gridLayout` so the surface/orientation→count decision lives in exactly
 *   one place.
 *
 * Reactive: re-renders the caller when the viewport crosses the portrait/landscape boundary
 * (via `useIsLandscape`'s resize listener). The surface answer is fixed for a given subtree.
 *
 * The landscape branch is a web-only enhancement (no Flutter parity — the LIVE Flutter app
 * uses a fixed 3-column grid on every surface, `DimensionConstants.gridThreeColumnsCount`),
 * so narrowing it to page surfaces is a web-UI decision and drops no Flutter behaviour.
 *
 * @returns the number of grid columns for the current surface and orientation
 *
 * @example
 * const columnsCount = useGridColumnsCount();
 */
export const useGridColumnsCount = (): number => {
  // Both hooks are called unconditionally, before any branch (Rules of Hooks).
  const isInSheetSurface = useIsInSheetSurface();
  const isLandscape = useIsLandscape();

  if (isInSheetSurface) return CONTENT_CONSTANTS.gridLayout.columnsCount;

  return isLandscape
    ? CONTENT_CONSTANTS.gridLayout.landscapeColumnsCount
    : CONTENT_CONSTANTS.gridLayout.columnsCount;
};
